import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { LinkButton, Tooltip } from '@nicheinc/campus-ds';

import { TableMapperType } from '../../types';

type PropsType = {
	item: any;
	mapperField: TableMapperType;
};

const LineClampedLinkButton = styled(LinkButton)<{ $lineClamp?: number }>`
	${({ $lineClamp }) =>
		$lineClamp &&
		`
	display: -webkit-box;
	-webkit-line-clamp: ${$lineClamp};
	-webkit-box-orient: vertical;
	overflow: hidden;
	white-space: normal;
	text-overflow: ellipsis;
	word-break: break-word;
  `}
`;

function LinkButtonCell({ item, mapperField }: PropsType) {
	const [textTruncated, setTextTruncated] = useState(false);
	const textRef = useRef<HTMLAnchorElement>(null);
	const linkText =
		typeof mapperField.linkText === 'function'
			? mapperField.linkText(item)
			: mapperField.linkText;

	const textTooltipText =
		typeof mapperField.textTooltipText === 'function'
			? mapperField.textTooltipText(item)
			: mapperField.textTooltipText;

	const textLineClamp =
		typeof mapperField?.lineClamp === 'function'
			? mapperField?.lineClamp(item)
			: mapperField?.lineClamp || 1;

	const linkOpenInNewTab =
		typeof mapperField.linkOpenInNewTab === 'function'
			? mapperField.linkOpenInNewTab(item)
			: mapperField.linkOpenInNewTab;

	const canRenderLink =
		!!linkText && (!!mapperField.onLinkClick || !!mapperField.getLinkHref);

	//Text length settings
	const textMaxLength = mapperField?.textMaxLength;

	//Truncate text in textMaxLength characters maximum, for some reason Text component noWrap and line clamp doesn't work inside the table cells
	const truncateText = (text: string) => {
		if (text && textMaxLength && text.length > textMaxLength) {
			return `${text.substring(0, textMaxLength).trim()}...`;
		}
		return text;
	};

	const getTooltipText = useCallback(() => {
		if (!textTruncated) {
			return '';
		}

		if (textTruncated) {
			return textTooltipText ? `${textTooltipText}` : `${linkText}`;
		}
	}, [linkText, textTooltipText, textTruncated]);

	useEffect(() => {
		// Check if text is truncated by maxLength config
		if (linkText && textMaxLength && linkText.length > textMaxLength) {
			setTextTruncated(true);
		}

		const textElement = textRef.current;

		const checkTruncation = () => {
			// Only check if we haven't already determined it's truncated
			if (!textTruncated && textElement) {
				const isTextElementTruncated =
					textElement.scrollHeight > textElement.clientHeight + 1;
				if (isTextElementTruncated) {
					setTextTruncated(true);
				}
			}
		};

		// Use requestAnimationFrame to ensure elements are rendered
		if (textElement) {
			requestAnimationFrame(checkTruncation);
		}

		const resizeObserver = new ResizeObserver(() => {
			requestAnimationFrame(checkTruncation);
		});

		if (textElement) resizeObserver.observe(textElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [linkText, textMaxLength, textTruncated, textLineClamp]);

	return (
		canRenderLink && (
			<Tooltip content={getTooltipText()}>
				<LineClampedLinkButton
					icon={mapperField.iconName}
					iconPosition={mapperField.iconPosition}
					onClick={() =>
						mapperField.onLinkClick && mapperField.onLinkClick(item)
					}
					href={mapperField.getLinkHref && mapperField.getLinkHref(item)}
					lineHeight="large"
					weight={mapperField.linkButtonTextWeight}
					$lineClamp={textLineClamp}
					ref={textRef}
					openInNewTab={linkOpenInNewTab}
				>
					{truncateText(linkText)}
				</LineClampedLinkButton>
			</Tooltip>
		)
	);
}

export default LinkButtonCell;
